import React, { useContext } from "react";
import StepContext from "./StepContext";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";
import {Button, ButtonDirection} from "../Button";

export const StepHeaderType = {
    NONE: 0,
    HORIZONTAL: 1,
    // VERTICAL: 2 // not implemented,
    HORIZONTAL_TIMELINE: 3
}

export function Step({ children }) {
    return <>{children}</>;
}

// allowBack is opt-in because some flows run side effects in onPreviousClick
export function StepHeader({ size = '', type, allowBack = false }) {
    const { steps, activeStep, setActiveStep } = useContext(StepContext);
    const { t } = useTranslation();

    const onStepClick = (event, index) => {
        event.preventDefault();
        if (allowBack && index < activeStep) setActiveStep(index);
    };

    const stepTitles = steps.map((child, idx) => ({
        label: child.props.title || t('stepper.steg', { stepNumber: idx + 1 }),
        id: idx
    }));
    
    return (
        <div className={`stepper${size}`}>
            <ul>
                {stepTitles?.map((step, index) => (
                    <li key={index} className={
                        activeStep > index && type !== StepHeaderType.HORIZONTAL_TIMELINE
                            ? "passed"
                            : activeStep === index
                                ? "active"
                                : ""
                    }>
                        <a href="#" onClick={(event) => onStepClick(event, index)}>
                            <span>{step.label}</span>
                        </a>
                    </li>
                ))}
            </ul>
        </div>
    );
}

/**
 * StepBody extracts and renders the content of the current step's StepBody.
 * Only one StepBody should be present per Step.
 */
export function StepBody() {
    const { currentStep } = useContext(StepContext);
    if (!currentStep) return null;
    const body = React.Children.toArray(currentStep?.props?.children)
        .find(child => child.type === StepBody);
    if (!body) {
        return null;
    }
    return body.props.children;
}

/**
 * StepFooter extracts and renders the content of the current step's StepFooter or StandardStepFooter.
 * Only one StepFooter/StandardStepFooter should be present per Step.
 */

export function StepFooter({children}) {
    const { currentStep } = useContext(StepContext);

    if (!currentStep) return null;

    if (children)
        return children;

    const footer = React.Children.toArray(currentStep?.props?.children)
        .find(child => child.type === StepFooter);

    if (footer)
        return footer.props.children;

    const standardFooter = React.Children.toArray(currentStep?.props?.children)
        .find(child => child.type === StandardStepFooter);

    if (standardFooter)
        return standardFooter; 

    return null;
}

export function StandardStepFooter({ children, onCancel, onNextClick, onPreviousClick, disabled, isBusy = false }) {
    const { onNext, onPrevious, isLastStep, isFirstStep } = useContext(StepContext);
    const { t } = useTranslation();

    const onNextHandler = () => {
        if (onNextClick?.() === false) return;
        onNext();
    };
    const onPreviousHandler = () => {
        if (onPreviousClick) onPreviousClick();
        onPrevious();
    };
    
    return (
        <StepFooter>
            <div className="d-flex w-100">
                {!isFirstStep() && <div className="me-auto">
                    <Button onClick={onPreviousHandler} direction={ButtonDirection.LEFT} disabled={isBusy}>{t('stepper.previous')}</Button>
                </div>}
                <div className="ms-auto d-flex gap-8 align-items-center">
                    {children}
                    {!isLastStep() && <Button direction={ButtonDirection.RIGHT} disabled={disabled || isBusy} onClick={onNextHandler}>{t('stepper.next')}</Button>}
                    {onCancel && <Button onClick={onCancel} disabled={isBusy}>{t('stepper.cancel')}</Button>}
                </div>
            </div>
        </StepFooter>
    );
}


